import { FC, useMemo } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import { Image, Skeleton } from '@chakra-ui/react';

import SunshineCard from 'components/_core/SunshineCard';
import SunshineDropdown from 'components/_core/SunshineDropdown';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import { useBankOverviewSearchParams } from 'features/Bank/pages/Overview/hooks/useBankOverviewSearchParams';
import * as workRoutes from 'features/Work/routes';

import type { DetailedTransaction } from '../../../../../../hooks/useDetailedTransactionQuery';
import useDeleteReceiptMutation from '../../../../hooks/useDeleteReceiptMutation';
import useUnlinkInvoiceMutation from '../../../../hooks/useUnlinkInvoiceMutation';
import locales from './locales';

interface LegacyAttachmentProps {
  invoiceId: string | null;
  fiscalId: string;
  transactionId: string;
  transactionType: string;
  receipt: DetailedTransaction['receipt'];
}

/**
 * @deprecated Replaced by Attachment
 * @todo Remove components after multi docs release
 * @link https://linear.app/shine/issue/CAC-58
 */
const LegacyAttachment: FC<LegacyAttachmentProps> = ({
  fiscalId,
  invoiceId,
  receipt,
  transactionId,
  transactionType,
}) => {
  const { getTransactionReceiptSearch } = useBankOverviewSearchParams();

  const [unlinkInvoice, { loading: unlinkInvoiceLoading }] =
    useUnlinkInvoiceMutation({ transactionId, transactionType });
  const [deleteReceipt, { loading: deleteReceiptLoading }] =
    useDeleteReceiptMutation({ transactionId, transactionType });

  const linkParams = useMemo<LinkProps['to']>(() => {
    const params: LinkProps['to'] = {
      state: {
        from: 'transaction-details',
      },
    };

    if (invoiceId) {
      params.pathname = workRoutes.getInvoice(invoiceId);
    } else {
      params.search = getTransactionReceiptSearch(transactionId);
    }

    return params;
  }, [invoiceId, transactionId, getTransactionReceiptSearch]);

  if (!invoiceId && !receipt) {
    return null;
  }

  const isInvoiceReceipt = !!invoiceId;
  const loading = unlinkInvoiceLoading || deleteReceiptLoading;

  const handleDelete = async () => {
    if (isInvoiceReceipt) {
      await unlinkInvoice(invoiceId);
    } else if (receipt) {
      await deleteReceipt(receipt.receiptId);
    }
  };

  return (
    <SunshineCard alignItems="center" as={Link} isBoxed={false} to={linkParams}>
      <SunshineCard.Slot name="aside">
        <Image
          borderRadius="12px"
          fallback={<SquircleIcon name="invoice" />}
          height="space-40"
          src={isInvoiceReceipt ? undefined : receipt?.url}
          width="space-40"
        />
      </SunshineCard.Slot>

      {loading ? (
        <Skeleton
          borderRadius="radius-10"
          endColor="grey.300"
          height="40px"
          startColor="grey.100"
          width="100%"
        />
      ) : (
        // Using an array because if we use a fragment "React childs" become 1
        // and we end up not rendering the SunshineCard.Slot
        [
          <SunshineCard.Content key="content">
            <SunshineCard.Title>
              {isInvoiceReceipt
                ? locales.formatString(locales.seeLinkedInvoice, { fiscalId })
                : locales.seeReceipt}
            </SunshineCard.Title>
          </SunshineCard.Content>,

          <SunshineCard.Slot key="action" name="action">
            <SunshineDropdown>
              <SunshineDropdown.Trigger>
                <SunshineCard.SunshineIcon
                  marginRight="space-8"
                  name="vertical-ellipsis"
                />
              </SunshineDropdown.Trigger>

              <SunshineDropdown.List>
                <SunshineDropdown.Item
                  colorScheme="red"
                  icon="trash"
                  onClick={async (event) => {
                    event.preventDefault();
                    handleDelete();
                  }}
                >
                  {isInvoiceReceipt ? locales.unlink : locales.delete}
                </SunshineDropdown.Item>
              </SunshineDropdown.List>
            </SunshineDropdown>
          </SunshineCard.Slot>,
        ]
      )}
    </SunshineCard>
  );
};

export default LegacyAttachment;
